import { Button, Card, Descriptions, Modal, message, Spin } from "antd";
import React, { Fragment } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import EntitledSection from "../components/EntitledSection";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import entitlements from "../config/entitlements";
import { TrusteeScheme } from "../helpers/shared-interfaces";
import { request } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import { DELETE_TRUSTEE_SCHEME, GET_TRUSTEE_SCHEME } from "../gql/trustees";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface RouteParams {
  id: string;
  schemeId: string;
}

interface State {
  isLoading: boolean;
  id: string | number;
  showDialog: boolean;
  trusteeId: string;
  trusteeScheme?: TrusteeScheme;
}

function ModalWrapper(
  showDialog: boolean,
  deleteTrusteeScheme: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteTrusteeScheme}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this scheme?</div>
    </Modal>
  );
}

export default class ViewTrusteeScheme extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      isLoading: true,
      id: "",
      showDialog: false,
      trusteeScheme: undefined,
      trusteeId: "",
    };
    this.deleteTrusteeScheme = this.deleteTrusteeScheme.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
  }

  componentDidMount(): void {
    this.setState(
      {
        id: this.props.match.params.schemeId,
        trusteeId: this.props.match.params.id
      }, (): void => {
        this.getTrusteeScheme();
      });
  }

  getTrusteeScheme = async (): Promise<void> => {
    const { id } = this.state;

    this.setState({ isLoading: true });

    try {
      const { data }: AxiosResponse<any> = await request({
        query: GET_TRUSTEE_SCHEME,
        variables: { id },
      });

      this.setState({ isLoading: false });

      if (data?.errors) {
        message.error("Failed to fetch scheme due to server errors.");
        return;
      }

      if (data?.data?.trusteeScheme) {
        this.setState({
          trusteeScheme: data.data.trusteeScheme,
        });
      } else {
        message.error("Scheme not found.");
      }
    } catch (error) {
      message.error(`An error occurred while fetching scheme. - ${generateDisplayErrorMessage(error)}`);
      this.setState({ isLoading: false });
    }
  };

  deleteTrusteeScheme(): void {
    const { id } = this.state;

    this.setState({ isLoading: true });

    request({
      query: DELETE_TRUSTEE_SCHEME,
      variables: { id },
    })
      .then(({ data }: AxiosResponse<any>): void => {
        this.setState({ isLoading: false, showDialog: false });
        if (!data.errors && data.data.deleteTrusteeScheme.result) {
          message.success("Trustee scheme deleted successfully");
          this.redirectToViewTrustee();
        } else {
          message.error("Failed to delete trustee scheme");
        }
      })
      .catch((): void => {
        this.setState({ isLoading: false, showDialog: false });
        message.error("An error occurred while deleting trustee scheme");
      });
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToViewTrustee(): void {
    this.props.history.push(`/trustees/${this.state.trusteeId}`);
  }

  render(): JSX.Element | null {
    const { isLoading, trusteeId, showDialog, trusteeScheme } = this.state;

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <div>
              {ModalWrapper(
                showDialog,
                this.deleteTrusteeScheme,
                this.toggleDeleteConfirmationModal,
              )}
              <Descriptions
                title={<h3>{trusteeScheme?.name}</h3>}
                extra={
                  <div>
                    <EntitledSection entitlement={entitlements.TrusteeSchemes.edit}>
                      <NavLink to={`/trustees/${trusteeId}/schemes/${trusteeScheme?.id}/edit?name=${trusteeScheme?.name}`}>
                        <Button
                          icon={<EditOutlined />}
                          size="large"
                          style={{
                            backgroundColor: "rgb(29, 55, 100)",
                            color: "white",
                            marginRight: "10px",
                          }}
                          data-cy="test-trustee-scheme-edit-button"
                        >
                          Edit
                        </Button>
                      </NavLink>
                    </EntitledSection>
                    <EntitledSection entitlement={entitlements.TrusteeSchemes.delete}>
                      <Button
                        icon={<DeleteOutlined />}
                        size="large"
                        onClick={this.toggleDeleteConfirmationModal}
                        data-cy="test-trustee-delete-button"
                      >
                        Delete
                      </Button>
                    </EntitledSection>
                  </div>
                }
              >
                <Descriptions.Item
                  label={<b>Trustee</b>}
                  key="Trustee Name"
                >
                  {trusteeScheme?.trustee?.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<b>Contact Person</b>}
                  key="Contact Person"
                >
                  {trusteeScheme?.trusteeContactPerson ? (
                    <span>
                      {trusteeScheme.trusteeContactPerson.firstName}{" "}
                      {trusteeScheme.trusteeContactPerson.lastName}
                      {", "}
                      {trusteeScheme.trusteeContactPerson.email}
                      {" ("}
                      <NavLink to={`/trustees/${trusteeId}`}>
                        manage on trustee
                      </NavLink>
                      {")"}
                    </span>
                  ) : (
                    <span>
                      None set.{" "}
                      <NavLink to={`/trustees/${trusteeId}`}>
                        Add one on the trustee
                      </NavLink>
                    </span>
                  )}
                </Descriptions.Item>
              </Descriptions>

            </div>
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
